import { Headline } from './headline.model';
import { CompanyBasic } from './company/company-basic.model';
import { CompanyFinancialData } from './company/company-financial-data.interface';
import { Portfolio } from './portfolio.model';
import { PositionType } from '../enums/position-type.enum';
import { StockType } from '../enums/stock-type.enum';
import { PositionDeltaStatus } from '../enums/position-delta-status.enum';
import { BasicUser } from './basicUser.model';
import { PositionAuditType } from '../enums/position-audit-type.enum';


export class Position {
  active?: boolean;
  company?: CompanyBasic;
  companyId: number;
  costBasis: number;
  created?: string;
  currencyCode?: string;
  description: string;
  discount: number;
  strikePrice: number;
  expiry: Date;
  editing?: boolean;
  headlines?: Array<Headline>;
  id?: number;
  isOpen?: boolean;
  intradaySummary?: {
    dailyPl: number;
    dailyPlInUsd: number;
    id: number;
    percentC: number;
    percentF: number;
    totalPl: number;
    totalValue: number;
    totalValueInUsd: number;
    ytdPercentChange: number;
    percentFRank: number;
    stockTypeRank: number;
    percentFRankSum: number;
    stockTypeRankSum: number;
  };
  manualPrice?: number;
  positionType?: string;
  isBoxed?: boolean;
  price?: {
    averageVolume: number;
    change: number;
    changeInUsd: number;
    companyId: number;
    currencyCode: string;
    lastTrade: number;
    lastTradeAt: string;
    lastTradeInUsd: number;
    percentChange: number;
    pricingType: string;
    volume: number;
  };
  timeToLiquidate?: number;
  shares?: number;
  stockType: string;
  stockTypeIcon?: string;
  updated?: string;
  isUpdating?: boolean;
  isAcquisitionAnnounced?: boolean;
  acquisitionAnnouncedById?: number;
  // UI only. The i18n key explaining why the ACQ A. checkbox is disabled, or '' when it is
  // enabled. Resolved once when the row lands rather than per change-detection pass.
  acqDisabledReasonKey?: string;
  usesManual?: boolean;
  showBoDCheck?: boolean;
  companyFinancial?: CompanyFinancialData;
  percentFund?: number;
  computed?: {
    fvMultiple?: number;
    dollarToFV?: number;
    wiwiiwMultiple?: number;
    percentToL?: number;
    blotterSide?: string;
    blotterSpread?: number;
    tenDayVolume?: number;
    fvPercent?: number;
    wiwiiwPercent?: number;
    fvMarketCap?: number;
  };
}


export class PositionRequestBody {
  companyId: number;
  costBasis: number;
  description: string;
  discount: number;
  manualPrice: any;
  positionType: string;
  shares: number;
  stockType: string;
  isBoxed?: boolean;
  isAcquisitionAnnounced?: boolean;
  acquisitionAnnouncedById?: number;
  expiry: Date;
  strikePrice: number;

  constructor(data: any, isAcquisitionAnnounced: boolean, acquisitionAnnouncedById?: number) {
    if (data) {
      this.companyId = data.company?.id;
      this.costBasis = data.costBasis;
      this.description = data.description;
      this.discount = data.discount || 0;
      this.positionType = data.positionType;
      this.shares = data.shares;
      this.stockType = data.stockType;
      this.manualPrice = this.setManualPrice(data);
      this.isBoxed = Boolean(data.isBoxed);
      this.expiry = data.expiry;
      this.strikePrice = data.strikePrice;
    }
    // Null is meaningful and must not be coerced. The backend treats a null flag as "the caller
    // did not state one" and leaves the company's announced pair alone; `|| false` would turn
    // every position save into a request to un-announce the company.
    this.isAcquisitionAnnounced = isAcquisitionAnnounced ?? null;
    this.acquisitionAnnouncedById = acquisitionAnnouncedById ?? null;
  }

  private setManualPrice(data: Position) {
    let value = null;

    if (data.isUpdating) {
      if ( data.stockType !== 'STOCK' && data.stockType !== 'UNREGISTERED' ) {
        value = data.manualPrice;
      }
    } else { // whenever we are creating a new Position
      if ( !(data.stockType === 'STOCK' || data.stockType === 'UNREGISTERED') ) {
        value = data.price;
      }
    }
    return value;
  }
}

export class PositionDelta {
  positionId: number;
  portfolio: Portfolio;
  company: CompanyBasic;
  description: string;
  stockType: StockType;
  edmStockType: StockType;
  price: number;
  edmPrice: number;
  shares: number;
  edmShares: number;
  costBasis: number;
  edmCostBasis: number;
  discount: number;
  positionType: PositionType;
  edmPositionType: PositionType;
  isBoxed: boolean;
  edmIsBoxed: boolean;
  deleted: boolean;
  created: boolean;
  status: PositionDeltaStatus;
  bulkSelect: boolean;

  constructor(data: any = {}) {
    this.positionId = data.positionId;
    this.portfolio = data.portfolio;
    this.company = new CompanyBasic(data.company);
    this.description = data.description;
    this.stockType = StockType[String(data.stockType)];
    this.edmStockType = StockType[String(data.edmStockType)];
    this.price = data.price;
    this.edmPrice = data.edmPrice;
    this.shares = data.shares;
    this.edmShares = data.edmShares;
    this.costBasis = data.costBasis;
    this.edmCostBasis = data.edmCostBasis;
    this.discount = data.discount;
    this.positionType = PositionType[String(data.positionType)];
    this.edmPositionType = PositionType[String(data.edmPositionType)];
    this.isBoxed = data.isBoxed;
    this.edmIsBoxed = data.edmIsBoxed;
    this.deleted = data.deleted;
    this.created = data.created;
    this.status = data.status ? PositionDeltaStatus[String(data.status)] : PositionDeltaStatus.ACCEPT;
    this.bulkSelect = false;
  }
}

export class ReturnPositionDelta {
  portfolioId: number;
  positionId: number;
  companyId: number;
  description: string;
  stockType: StockType;
  manualPrice: number;
  shares: number;
  costBasis: number;
  discount: number;
  isAcquisitionAnnounced: boolean;
  acquisitionAnnouncedById: number;
  positionType: PositionType;
  isBoxed: boolean;
  isIgnored: boolean;
  isDeleted: boolean;
  isCreated: boolean;

  constructor(data: PositionDelta) {
    this.portfolioId = data.portfolio.id;
    this.positionId = data.positionId;
    this.companyId = data.company.id;
    this.description = data.description;
    this.stockType = this.isNewEdmValue(data.stockType, data.edmStockType) ? data.edmStockType : data.stockType;
    this.manualPrice = this.isNewEdmValue(data.price, data.edmPrice) ? data.edmPrice : data.price;
    this.shares = this.isNewEdmValue(data.shares, data.edmShares) ? data.edmShares : data.shares;
    this.costBasis = this.isNewEdmValue(data.costBasis, data.edmCostBasis) ? data.edmCostBasis : data.costBasis;
    this.discount = data.discount;
    // Deliberately null, not echoed. Reconcile has no announced control, so it has no opinion,
    // and the backend leaves the pair alone. Echoing the company's value looked harmless but was
    // not: a company object without the field reads undefined, which arrived as false and made
    // every reconcile silently un-announce the companies it touched.
    this.isAcquisitionAnnounced = null;
    this.acquisitionAnnouncedById = null;
    this.positionType = this.isNewEdmValue(data.positionType, data.edmPositionType) ? data.edmPositionType : data.positionType;
    this.isBoxed = Boolean(this.isNewEdmValue(data.isBoxed, data.edmIsBoxed) ? data.edmIsBoxed : data.isBoxed);
    this.isIgnored = data.status === PositionDeltaStatus.IGNORE;
    this.isDeleted = data.deleted;
    this.isCreated = data.created;
  }

  private isNewEdmValue(rmsValue: any, edmValue: any) {
    return edmValue !== null && edmValue !== undefined && edmValue !== rmsValue;
  }
}

export class PositionAudit {
  portfolio: Portfolio;
  company: CompanyBasic;
  description: string;
  type: PositionAuditType;
  trailDescription: string;
  created: Date;
  createdBy: BasicUser;
  updated: Date;
  updatedBy: BasicUser;

  constructor(data: any = {}) {
    this.portfolio = data.portfolio;
    this.company = data.company ? new CompanyBasic(data.company) : null;
    this.description = data.description;
    this.type = PositionAuditType[String(data.type)];
    this.trailDescription = data.trailDescription;
    this.created = new Date(data.created);
    this.createdBy = new BasicUser(data.createdBy);
    this.updated = new Date(data.updated);
    this.updatedBy = new BasicUser(data.updatedBy);
  }
}
